import './LoiPreparationModal.scss';
import React, { useEffect, useState } from 'react';
import { ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../components/Rodal/Rodal';
import { useStateSelector } from '../../../store/selectors';
import { fetchLoiPreparationUrl, fetchLoiPreparationStatus } from '../../../slices/marketplace/loi-preparation/loi-preparation-api';
import Helpers from '../../../utils/helper';
import createNotification from '../../../utils/createNotification';

const LOI_PREPARATION_MODAL_WIDTH = 1200;

export interface LoiPreparationModalProps {
    isVisible: boolean;
    onClose: () => void;
    engagementId: number;
    eventId: string;
    buyerId: number;
}

export const LoiPreparationModal = (props: LoiPreparationModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [prepareUrl, setPrepareUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!props.isVisible || !axios) {
            return;
        }
        setIsLoading(true);
        setPrepareUrl(null);
        setErrorMessage(null);
        fetchLoiPreparationUrl(axios, props.engagementId, props.eventId, props.buyerId)
            .then((url) => {
                setPrepareUrl(url);
            })
            .catch((err: unknown) => {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Could not load LOI preparation. Please try again.';
                setErrorMessage(message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [props.isVisible, axios, props.engagementId, props.eventId, props.buyerId]);

    useEffect(() => {
        if (!props.isVisible || !axios) {
            return;
        }
        let cancelled = false;
        const intervalId = setInterval(async () => {
            if (cancelled) return;
            try {
                const isPrepared = await fetchLoiPreparationStatus(
                    axios,
                    props.engagementId,
                    props.eventId,
                    props.buyerId,
                );
                if (isPrepared && !cancelled) {
                    createNotification('LOI prepared successfully!', 'success');
                    props.onClose();
                }
            } catch {
            }
        }, 3000);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [props.isVisible, axios, props.engagementId, props.eventId, props.buyerId, props.onClose]);

    const renderBody = () => {
        if (isLoading) {
            return (
                <div className="loi-preparation-loading">
                    {Helpers.renderTableLoader()}
                </div>
            );
        }
        if (errorMessage) {
            return <div className="loi-preparation-error">{errorMessage}</div>;
        }
        if (prepareUrl) {
            return (
                <iframe
                    className="loi-preparation-iframe"
                    src={prepareUrl}
                    title="LOI Preparation"
                />
            );
        }
        return null;
    };

    return (
        <div className="loi-preparation-modal">
            <Rodal
                visible={props.isVisible}
                onClose={props.onClose}
                className="checkout-modal loi-preparation-rodal"
                width={LOI_PREPARATION_MODAL_WIDTH}
                customStyles={{ maxWidth: '90vw' }}
                animation="fade"
                showMask={true}
                centered>
                <ModalHeader>
                    <span>Prepare LOI for Signing</span>
                </ModalHeader>
                <ModalBody>
                    {renderBody()}
                </ModalBody>
            </Rodal>
        </div>
    );
};
